import {
  ChartIcon,
  CheckIcon,
  GKButton,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
  NumberIcon,
  RupeeIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  UserIcon,
} from "components";
import { map } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { GKModalProps } from "utils/commonInterface";
import { Cell, TableBodyCell } from "../utils";
import { CommissionListObjectInterface } from "interfaces/Revenue";
import { numberToCurrency } from "utils";
import { useMutation } from "hooks/useQueries";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";

interface Props {
  commissionDetails: CommissionListObjectInterface;
}

const CommissionDetailsModal = (props: GKModalProps & Props) => {
  const { open, setOpen, commissionDetails } = props;

  const cardDetails = [
    {
      name: "Partner Name",
      value: commissionDetails?.partnerName || "-",
      icon: UserIcon,
    },
    {
      name: "Portfolio Name - Code:",
      value: `${commissionDetails?.portfolioName} - ${commissionDetails?.portfolioId}`,
      icon: ChartIcon,
    },
    {
      name: "Invoice No:",
      value: commissionDetails?.invoiceNumber || "-",
      icon: NumberIcon,
    },
    {
      name: "Invoice Amount:",
      value: numberToCurrency(+commissionDetails?.invoiceAmount),
      icon: RupeeIcon,
    },
  ];

  const form = useForm({
    defaultValues: {
      id: commissionDetails?.id || "",
      paidManagementFeeCommission:
        commissionDetails?.paidManagementFeeCommission || "",
      paidPerformanceFeeCommission:
        commissionDetails?.paidPerformanceFeeCommission || "",
      paidAmount: commissionDetails?.paidAmount || "",
      status: commissionDetails.status || "",
    },
  });

  const managementPaid = +form.watch("paidManagementFeeCommission") || 0;
  const performancePaid = +form.watch("paidPerformanceFeeCommission") || 0;
  const totalPaid = managementPaid + performancePaid;

  const handleCommissionPaid = useMutation({
    mutationKey: ["PAY_COMMISSION"],
    mutationFn: (data) =>
      axiosInstance.patch(
        `/api/revenue/revenue-commission-manager/bulk-update/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("COMMISSION_MANAGER_LIST");
      setOpen(false);
    },
  });

  const handleCloseModal = () => setOpen(false);

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle="Commission Details"
      width={"620px"}
      maxHeight="calc(100vh - 260px)"
      footer={
        <Grid2
          container
          size={12}
          display={"flex"}
          justifyContent={"space-between"}
        >
          <GKButton variant="outlined" color="dark" onClick={handleCloseModal}>
            Cancel
          </GKButton>
          <GKButton
            variant="contained"
            startIcon={<CheckIcon />}
            type="submit"
            form="update-commission-form"
            loading={handleCommissionPaid.isLoading}
          >
            Save Changes
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="update-commission-form"
        onSubmit={form.handleSubmit((values) => {
          const paidAmount =
            (+values.paidManagementFeeCommission || 0) +
            (+values.paidPerformanceFeeCommission || 0);

          handleCommissionPaid.mutate([{ ...values, paidAmount }]);
        })}
      >
        <Grid2 container spacing={4} py={1}>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            p={"12px"}
            borderRadius={1}
            bgcolor={"var(--grey-bg-100)"}
          >
            <Grid2 container size={9} spacing={"12px"}>
              {map(cardDetails, (item) => (
                <Grid2
                  size={12}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"4px"}
                >
                  <item.icon color="var(--neutral-500)" />
                  <Typography
                    fontWeight={500}
                    color="var(--neutral-500)"
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    {item.name}
                    <Typography fontWeight={500} color="var(--black)">
                      {item.value}
                    </Typography>
                  </Typography>
                </Grid2>
              ))}
            </Grid2>
            <Grid2 size={3} height={"100%"}>
              <Controller
                name="status"
                control={form.control}
                render={({ field }) => (
                  <GKSearchSelect
                    size="small"
                    options={[
                      { name: "Unpaid", value: "Pending" },
                      { name: "Paid", value: "Paid" },
                    ]}
                    getOptionLabel={(opt) => opt?.name || opt}
                    onChange={(event, val) => field.onChange(val?.value || "")}
                    value={
                      [
                        { name: "Unpaid", value: "Pending" },
                        { name: "Paid", value: "Paid" },
                      ].find((item) => item.value === field.value) || null
                    }
                  />
                )}
              />
            </Grid2>
          </Grid2>
          <Grid2 container size={12} spacing={2}>
            <Grid2 size={12}>
              <Typography
                fontSize={12}
                fontWeight={600}
                color="var(--neutral-500)"
                textTransform={"uppercase"}
              >
                management Fees details:
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ "&.MuiTableRow-head th": { p: "12px" } }}>
                      <Cell align="right" style={{ width: "30%" }}>
                        management Fees
                      </Cell>
                      <Cell align="right" style={{ width: "30%" }}>
                        Commission for Mgt. fees
                      </Cell>
                      <Cell style={{ width: "40%" }}>To be paid</Cell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "&.MuiTableRow-head th": { p: "12px" } }}>
                      <TableBodyCell align="right">
                        {numberToCurrency(+commissionDetails?.managementFee)}
                      </TableBodyCell>
                      <TableBodyCell align="right">
                        {" "}
                        {numberToCurrency(
                          +commissionDetails?.managementFeeCommission,
                        )}
                      </TableBodyCell>
                      <TableBodyCell>
                        <Controller
                          control={form.control}
                          name="paidManagementFeeCommission"
                          render={({ field }) => (
                            <GKTextField
                              {...field}
                              size="small"
                              placeholder="To Be Paid"
                              slotProps={{
                                input: { endAdornment: <RupeeIcon /> },
                              }}
                            />
                          )}
                        />
                      </TableBodyCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>
          </Grid2>
          <Grid2 container size={12} spacing={2}>
            <Grid2 size={12}>
              <Typography
                fontSize={12}
                fontWeight={600}
                color="var(--neutral-500)"
                textTransform={"uppercase"}
              >
                performance Fees details:
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ "&.MuiTableRow-head th": { p: "12px" } }}>
                      <Cell align="right" style={{ width: "30%" }}>
                        performance Fees
                      </Cell>
                      <Cell align="right" style={{ width: "30%" }}>
                        Commission for Perf. fees
                      </Cell>
                      <Cell style={{ width: "40%" }}>To be paid</Cell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "&.MuiTableRow-head th": { p: "12px" } }}>
                      <TableBodyCell align="right">
                        {numberToCurrency(+commissionDetails?.performanceFee)}
                      </TableBodyCell>
                      <TableBodyCell align="right">
                        {numberToCurrency(
                          +commissionDetails?.performanceFeeCommission,
                        )}
                      </TableBodyCell>
                      <TableBodyCell>
                        <Controller
                          control={form.control}
                          name="paidPerformanceFeeCommission"
                          render={({ field }) => (
                            <GKTextField
                              {...field}
                              size="small"
                              placeholder="To Be Paid"
                              slotProps={{
                                input: { endAdornment: <RupeeIcon /> },
                              }}
                            />
                          )}
                        />
                      </TableBodyCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>
          </Grid2>
          <Grid2
            container
            size={12}
            spacing={0}
            borderRadius={2}
            bgcolor={"var(--grey-bg-100)"}
          >
            <Grid2
              size={8}
              display={"flex"}
              justifyContent={"flex-end"}
              p="16px 12px"
            >
              <Typography fontSize={14} fontWeight={600} color="var(--black)">
                Total Commission to be paid:
              </Typography>
            </Grid2>
            <Grid2 size={4} p="16px 20px">
              <Typography fontSize={14} fontWeight={600} color="var(--black)">
                {numberToCurrency(totalPaid)}
              </Typography>
            </Grid2>
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default CommissionDetailsModal;
